<template>
  <!-- Bare, box-property-free wrapper: the panel's spacing is entirely mt-2/mt-3 margins in block
       flow, so this div is layout-neutral (margins collapse through it) and the panel renders
       identically to the inline markup it replaced in EditSqlViewWidget. Keep it free of padding /
       border / display:flex / overflow or the spacing shifts. -->
  <div>
    <span class="viz-sub-label mt-3">Color schema</span>
    <v-select
      :value="schemaId"
      :items="selectItems"
      :loading="libraryLoading"
      item-text="text"
      item-value="value"
      placeholder="Choose a schema"
      outlined
      dense
      hide-details
      class="viz-narrow-select"
      @input="$emit('schema-input', $event)"
      @change="$emit('schema-change', $event)">
    </v-select>

    <!-- Selected built-in or library schema: an optional value-unit (shown only when the measure could
         be in a different unit than the schema's bands), a read-only band preview, and, for a restored
         legacy inline schema (no longer creatable), a note to pick a shared schema instead. -->
    <template v-if="showDetails">
      <span v-if="showBandBadge" class="schema-badge mt-2">
        <v-icon x-small class="mr-1">mdi-information-outline</v-icon>Default inspection bands
      </span>

      <template v-if="unitItems.length > 1">
        <span class="viz-sub-label mt-3">Value unit</span>
        <v-select
          :value="valueUnit"
          :items="unitItems"
          item-text="text"
          item-value="value"
          outlined
          dense
          hide-details
          class="viz-narrow-select"
          @input="$emit('unit-input', $event)">
        </v-select>
        <span class="viz-sub-hint">{{ unitHint }}</span>
      </template>

      <div v-if="bandPreview" class="schema-preview mt-3">
        <div class="schema-preview__strip">
          <span
            v-for="(band, bi) in bandPreview"
            :key="bi"
            class="schema-preview__band">
            <span class="schema-preview__swatch" :style="{ background: band.swatch }"></span>
            {{ band.label }}
          </span>
        </div>
      </div>

      <span v-if="isCustom" class="viz-sub-hint mt-2">
        This custom schema was built for this widget. Pick a shared schema to replace it.
      </span>
    </template>

    <!-- Reference whose schema was removed: block save with friendly copy. -->
    <p v-else-if="missingReference" class="viz-threshold-error warning--text mt-2" role="alert">
      <v-icon x-small color="warning" class="mr-1">mdi-alert-circle-outline</v-icon>
      This schema was removed. Pick another color schema.
    </p>
  </div>
</template>

<script>
/* eslint-disable */
// Presentational "By schema" color-schema picker, extracted verbatim from EditSqlViewWidget so the
// count-KPI and chart call sites render identically without duplicating ~55 lines each. All business
// logic (which schemas apply, the band preview, missing/loading state) stays in the parent and is
// passed down as props; the child only forwards the schema / value-unit edits back up as events. The
// two call sites differ ONLY by the `unit-hint` string.
export default {
  name: 'SchemaPicker',

  props: {
    // The currently selected schema id (built-in id, library uuid, or the legacy-inline sentinel).
    schemaId: { type: String, default: '' },
    // The value-unit the KPI/measure is in, converted onto the schema's bands.
    valueUnit: { type: String, default: '' },
    // Call-site-specific caption under the Value unit select (KPI value vs chart measure wording).
    unitHint: { type: String, default: '' },
    // Schema dropdown options (parent's kpiSchemaSelectItems).
    selectItems: { type: Array, default: () => [] },
    // True while the tab's library schemas are still loading (dropdown shows a loading item).
    libraryLoading: { type: Boolean, default: false },
    // True when a resolved schema (or a legacy inline) is selected → show the detail block.
    showDetails: { type: Boolean, default: false },
    // True for a NUMBER built-in inspection scale → show the "Default inspection bands" badge.
    showBandBadge: { type: Boolean, default: false },
    // Value-unit options (only shown when there is more than one convertible unit).
    unitItems: { type: Array, default: () => [] },
    // Read-only band swatches/labels for the selected schema (null when nothing to preview).
    bandPreview: { type: Array, default: null },
    // True when the selection is a restored legacy inline schema (read-only, replace-me hint).
    isCustom: { type: Boolean, default: false },
    // True when the picked reference's schema is gone (blocks save with a friendly message).
    missingReference: { type: Boolean, default: false },
  },
};
</script>

<style lang="scss" scoped>
.viz-sub-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 6px;
  opacity: 0.75;
}

.viz-sub-hint {
  display: block;
  margin-top: 6px;
  font-size: 11.5px;
  opacity: 0.6;
}

.viz-narrow-select {
  max-width: 340px;
}

// Inline validation message. Colour comes from Vuetify `warning--text`; this class only owns layout.
.viz-threshold-error {
  display: flex;
  align-items: center;
  margin-bottom: 0;
  font-size: 12px;
  line-height: 1.4;
}

// "By schema" panel: a subtle facility-bands badge + a read-only band preview strip. Flat surfaces
// with thin outlines only (no tinted washes, no accent bars).
.schema-badge {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  padding: 2px 9px;
  font-size: 11.5px;
  font-weight: 500;
  opacity: 0.75;
  border: 1px solid #e2e6e4;
  border-radius: var(--ds-radius-full);
}

.schema-preview__strip {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 10px;
}

.schema-preview__band {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  opacity: 0.85;
}

.schema-preview__swatch {
  display: inline-block;
  width: 11px;
  height: 11px;
  border-radius: 3px;
  margin-right: 5px;
  flex: 0 0 auto;
  border: 1px solid rgba(0, 0, 0, 0.15);
}

.theme--dark {
  .schema-badge {
    border-color: rgba(255, 255, 255, 0.18);
  }

  .schema-preview__swatch {
    border-color: rgba(255, 255, 255, 0.2);
  }
}
</style>
